import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Frame,
  Icon,
  ProgressBar,
  SageClassnames,
  useSageBreakpoint,
} from '@kajabi/sage-react';
import min from 'lodash/min';

import { MockBrowser } from './MockBrowser';

const PREVIEW_LOADER_STYLE = `
  .mock-browser-contracted {
    height: 0;
    max-height: 0;
    transition-property: all;
    transition-duration: .5s;
    transition-timing-function: cubic-bezier(1, 1, 1, 1);
    overflow-y: hidden;
    visibility: hidden;
  }

  .mock-browser-contracted.expanded {
    height: auto;
    max-height: 1000px;
    visibility: visible;
  }

  .preview-loader-description {
    overflow-y: hidden;
    max-height: 200px;
    transition-property: all;
    transition-duration: .5s;
    transition-timing-function: cubic-bezier(1, 1, 1, 1);
  }

  .preview-loader-description.closed {
    max-height: 0;
  }
`;

const COMPLETE_STEP = { title: 'Complete!' };

const setIntervalX = (callback, delay, repetitions) => {
  var x = 0;
  var intervalID = window.setInterval(() => {
    callback();
    if (++x === repetitions) {
      window.clearInterval(intervalID);
    }
  }, delay);
};

export const PreviewLoader = ({ onComplete, intervalTime, stepsData }) => {
  const breakpoints = useSageBreakpoint();

  const intervalRef = useRef();

  const [step, setStep] = useState(0);
  const [contentStep, setContentStep] = useState(0);

  // Injecting a complete step
  const allSteps = useMemo(
    () =>
      stepsData.map((data) => ({
        ...data,
        content: [...data.content, COMPLETE_STEP],
      })),
    [stepsData]
  );

  const isLastStep = useMemo(
    () => allSteps.length === step + 1,
    [step, allSteps]
  );
  const isLastContent = useMemo(
    () => allSteps[step].content.length === contentStep + 1,
    [allSteps, step, contentStep]
  );

  const hideOrExpandSection = useCallback(
    (stepInBrowser) => {
      return [
        'mock-browser-contracted',
        contentStep >= stepInBrowser && 'expanded',
      ].filter(Boolean);
    },
    [contentStep]
  );

  useEffect(() => {
    if (!intervalRef.current) {
      const intervals = allSteps[step].content.length - 1;
      setIntervalX(
        () => {
          setContentStep((previousContentStep) => min([previousContentStep + 1, intervals]));
        },
        intervalTime,
        intervals
      );
      intervalRef.current = true;
    }
  }, [allSteps, intervalTime, step]);

  useEffect(() => {
    if (isLastContent) {
      const stepTimer = setTimeout(() => {
        if (isLastStep) {
          onComplete();
        } else {
          intervalRef.current = false;
          setStep((previousStep) => min([previousStep + 1, allSteps.length - 1]));
          setContentStep(0);
        }
      }, intervalTime);

      return () => {
        clearTimeout(stepTimer);
      };
    }

  }, [allSteps, intervalTime, isLastStep, isLastContent, onComplete]);

  const Component = useMemo(() => allSteps[step].component, [allSteps, step]);

  return (
    <Frame
      direction={Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']}
      style={{
        height: '100%',
        overflowY: 'hidden',
        paddingTop: breakpoints.sm ? 12 : 64,
        paddingRight: breakpoints.sm ? 12 : 64,
        paddingLeft: breakpoints.sm ? 12 : 64,
      }}
    >
      <style children={PREVIEW_LOADER_STYLE} />
      <Frame
        align={Frame.ALIGNMENTS.CENTER_LEFT}
        padding={Frame.PADDINGS[breakpoints.sm ? 'XS' : '2XL']}
        style={breakpoints.sm ? {} : { height: '100%', width: '40%' }}
      >
        <Frame gap={Frame.GAPS.NONE}>
          <Frame
            align={Frame.ALIGNMENTS.CENTER_LEFT}
            direction={Frame.DIRECTIONS.HORIZONTAL}
            gap={Frame.GAPS.XS}
          >
            {isLastContent && (
              <Icon
                color={Icon.COLORS.SAGE_300}
                icon={Icon.ICONS.CHECK_CIRCLE_FILLED}
                size={Icon.SIZES['XL']}
              />
            )}
            <h5 className={SageClassnames.TYPE.HEADING_5}>
              Step {step + 1}:&nbsp;
              <span>{allSteps[step].content[contentStep]?.title}</span>
            </h5>
          </Frame>
          <div>
            <p
              className={[
                SageClassnames.TYPE.BODY_SM,
                SageClassnames.TYPE_COLORS.CHARCOAL_200,
                'preview-loader-description',
                isLastContent && 'closed',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {allSteps[step].content[contentStep]?.description}
            </p>
          </div>
        </Frame>
        <ProgressBar
          disableTooltip
          percent={((contentStep + 1) / allSteps[step].content.length) * 100}
          style={{ width: '100%' }}
        />
      </Frame>
      <MockBrowser
        fadeOut={isLastContent && !isLastStep}
        intervalTime={intervalTime}
      >
        <Component
          breakpoints={breakpoints}
          hideOrExpandSection={hideOrExpandSection}
          currentStep={contentStep}
        />
      </MockBrowser>
    </Frame>
  );
};

PreviewLoader.defaultProps = {
  intervalTime: 2500,
};

PreviewLoader.propTypes = {
  onComplete: PropTypes.func.isRequired,
  intervalTime: PropTypes.number,
  stepsData: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.func.isRequired,
      content: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
      })).isRequired,
    })
  ).isRequired,
};
